import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import { removeSuperAdminCallable } from '@/lib/callables';

type Props = {
  open: boolean;
  uid: string | null;
  email: string | null;
  onClose: () => void;
  /**
   * Chamado depois que a callable confirmou (antes de fechar), para a tela
   * recarregar a lista do Auth (#795, ADM-6). Cancelar não chama.
   */
  onSuccess?: () => void;
};

/**
 * Diálogo "Remover": revoga `superAdmin` E `adminRole` via `removeSuperAdmin`.
 *
 * Origem: 1f372df7a — 2026-04-26 17:49 +0200 (Sprint 2; sem issue no commit).
 *
 * Contrato: exige motivo no cliente (o servidor revalida). Desde a #795 a
 * callable é ROOT-only e recusa tirar a última ROOT (ADM-2) — a mensagem do
 * servidor aparece num `Alert` dentro do modal (#1021).
 *
 * Usado por: `routes/SuperAdminsPage.tsx`.
 *
 * Histórico:
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function RemoveSuperAdminDialog({
  open,
  uid,
  email,
  onClose,
  onSuccess,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close(): void {
    setReason('');
    setError(null);
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    if (!uid) return;
    if (reason.trim().length === 0) {
      setError(t('tenantDetail.actions.reasonRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await removeSuperAdminCallable({ uid, reason: reason.trim() });
      onSuccess?.();
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
      header={t('superAdmins.remove.title')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('tenantDetail.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void handleSubmit();
              }}
              disabled={uid === null}
              loading={submitting}
            >
              {t('superAdmins.remove.confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          {t('superAdmins.remove.description', { target: email ?? uid ?? '—' })}
        </Box>
        <FormField label={t('tenantDetail.actions.reasonLabel')}>
          <Textarea
            value={reason}
            onChange={({ detail }): void => {
              setReason(detail.value);
              // #1021 (2026-10-25 15:00 +0100): digitar o motivo tira o "motivo obrigatório" do Alert.
              setError(null);
            }}
            rows={3}
            placeholder={t('tenantDetail.actions.reasonPlaceholder')}
          />
        </FormField>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do
            overlay do Modal (mesmo achado do CRM, #174) — quem clicava no botão não via nada.
            Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
